/**
 * @file Ecran Armes : slots equipes (un chat par arme), coffre (6+10), tirages normal/epic (x1/x10), fusion glisser-deposer, fusion auto, verrou, collection, etoiles.
 */
import { stageValueToDisplay } from '@core/index';
import { formatBig } from '@shared/format';
import type { Uid } from '@shared/types';
import { Component } from '../core/Component';
import { h, setText, toggleClass } from '../core/dom';
import { notify, send } from '../core/feedback';
import { uiIcon } from '../core/uiIcons';
import { makeTabs, sheetHeader } from '../core/widgets';
import type { TabsHandle } from '../core/widgets';
import type { UiContext } from '../UiContext';
import { CollectionPane } from './weapons/CollectionPane';
import { GachaReveal } from './weapons/GachaReveal';
import { InventoryPane } from './weapons/InventoryPane';
import type { WeaponsShared } from './weapons/InventoryPane';
import { WeaponDrag } from './weapons/drag';
import type { DragSource, DropTarget } from './weapons/drag';
import { PopupMenu } from './weapons/menu';
import { StarsPane } from './weapons/StarsPane';
import { WeaponDetail } from './weapons/WeaponDetail';
import { createTile } from './weapons/tile';

type TabId = 'inventory' | 'collection' | 'stars';

interface SlotView {
  slot: number;
  root: HTMLElement;
  tileHost: HTMLElement;
  /** Emplacement verrouille : cadenas + stage de deblocage. */
  lock: HTMLElement;
  lockText: HTMLElement;
  /** Les 3 lignes de caracteristiques (arme equipee) : degats par tir, vitesse de tir, DPS. */
  stats: HTMLElement;
  dmg: HTMLElement;
  speed: HTMLElement;
  dps: HTMLElement;
  /** defId affiche dans `tileHost` (reconstruit seulement quand l'arme change). */
  shown: string | null;
}

/** Cadence de tir lisible : 1 decimale sous 10 tirs/s, entier au-dela. */
const formatShotsPerSecond = (v: number): string => (v >= 10 ? Math.round(v).toString() : v.toFixed(1));

export class WeaponsScreen extends Component {
  private tabs!: TabsHandle<TabId>;
  private readonly slots: SlotView[] = [];
  private inventory!: InventoryPane;
  private collection!: CollectionPane;
  private stars!: StarsPane;
  private reveal!: GachaReveal;
  private menu!: PopupMenu;
  private readonly panes = new Map<TabId, HTMLElement>();
  private selectedUid: Uid | null = null;
  private drag!: WeaponDrag;
  private detail!: WeaponDetail;
  private lastMerged: Uid | null = null;

  constructor(ctx: UiContext) {
    super(ctx, 'div', 'screen weapons');
  }

  protected build(): void {
    const t = this.ctx.i18n;
    this.el.setAttribute('data-ui-block', '');
    this.menu = new PopupMenu(this.el);
    this.addDisposer(() => this.menu.close());

    // glisser-deposer commun : coffre -> emplacement (equiper), emplacement -> coffre (retirer), emplacement <-> emplacement / arme (echanger), arme -> arme (fusionner)
    this.drag = new WeaponDrag(this.el, {
      start: () => this.menu.close(),
      canDrop: (src, target) => this.canDrop(src, target),
      drop: (src, target) => this.dropOn(src, target),
      tap: (src) => (src.slot === null ? this.select(this.selectedUid === src.uid ? null : src.uid) : this.tapSlot(src.slot)),
      longPress: (src) => this.openMenu(src.uid, src.el),
      lift: () => this.ctx.haptic('tick'),
    });
    this.addDisposer(() => this.drag.destroy());

    const shared: WeaponsShared = {
      selected: () => this.selectedUid,
      select: (uid) => this.select(uid),
      equip: (uid, slot) => this.equip(uid, slot),
      autoSlot: () => this.autoSlot(),
      drag: this.drag,
      openMenu: (uid, anchor) => this.openMenu(uid, anchor),
    };

    // ---- emplacements equipes (toujours visibles : cible des equipements / glisser-deposer)
    const slotRow = h('div', { class: 'slots' });
    for (let slot = 0; slot < this.ctx.config.balance.equipSlotUnlocks.length; slot++) {
      const tileHost = h('div', { class: 'slot-tile' });
      const lockText = h('span', { class: 'slot-lock-text' });
      const lock = h('div', { class: 'slot-lock is-hidden' }, uiIcon('lock', 'ui-ico slot-lock-ico'), lockText);
      const dmg = h('b', { class: 'stat-val' });
      const speed = h('b', { class: 'stat-val' });
      const dps = h('b', { class: 'stat-val' });
      const row = (icon: Node | string, title: string, value: HTMLElement): HTMLElement =>
        h('div', { class: 'stat-row', title }, h('span', { class: 'stat-ico' }, icon), value);
      const stats = h(
        'div',
        { class: 'slot-stats is-hidden' },
        row(uiIcon('power'), t.t('stat.damage'), dmg),
        row(uiIcon('speed'), t.t('stat.attackSpeed'), speed),
        row('DPS', t.t('stat.dps'), dps),
      );
      const root = h('div', { class: 'slot', role: 'button', tabindex: 0, 'data-slot': slot, 'data-ui-block': true }, tileHost, lock, stats);
      // l'emplacement est glissable quand il porte une arme (retirer / echanger) ; vide ou verrouille : seulement un tap
      this.drag.attach(root, () => {
        const uid = this.ctx.sim.data.weapons.equipped[slot] ?? null;
        return { uid: uid ?? '', slot, el: root, draggable: uid !== null };
      });
      root.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.tapSlot(slot);
        }
      });
      root.addEventListener('contextmenu', (e) => e.preventDefault());
      this.slots.push({ slot, root, tileHost, lock, lockText, stats, dmg, speed, dps, shown: null });
      slotRow.append(root);
    }

    // ---- onglets
    this.tabs = makeTabs<TabId>(
      [
        { id: 'inventory', label: t.t('weapons.tab.inventory') },
        { id: 'collection', label: t.t('weapons.tab.collection') },
        { id: 'stars', label: t.t('weapons.tab.stars') },
      ],
      'inventory',
      () => this.onTab(),
    );
    const body = h('div', { class: 'sheet-body' });
    for (const id of ['inventory', 'collection', 'stars'] as const) {
      const pane = h('div', { class: `pane pane-${id}` });
      this.panes.set(id, pane);
      body.append(pane);
    }
    this.inventory = new InventoryPane(this.ctx, shared).mount(this.panes.get('inventory') as HTMLElement);
    this.collection = new CollectionPane(this.ctx).mount(this.panes.get('collection') as HTMLElement);
    this.stars = new StarsPane(this.ctx).mount(this.panes.get('stars') as HTMLElement);
    this.reveal = new GachaReveal(this.ctx).mount(this.el);
    for (const c of [this.inventory, this.collection, this.stars, this.reveal]) this.addDisposer(() => c.destroy());

    // Panneau de details : toucher une arme (coffre ou emplacement) la selectionne ET affiche ses caracteristiques + actions.
    this.detail = new WeaponDetail(this.ctx, {
      selected: () => this.selectedUid,
      close: () => this.select(null),
      targetSlot: () => this.autoSlot(),
      equip: (uid, slot) => this.equip(uid, slot),
      merged: () => this.select(this.lastMerged),
    });
    this.addDisposer(() => this.detail.destroy());
    this.listen('WeaponsMerged', (e) => {
      this.lastMerged = e.result;
    });

    this.listen('WeaponDrawn', (e) => this.reveal.show(e.defIds));
    this.el.append(sheetHeader(this.ctx, t.t('weapons.title')), slotRow);
    this.detail.mount(this.el);
    this.el.append(this.tabs.el, body);
    // Le calque de revelation reste le dernier enfant (au-dessus de tout).
    this.el.append(this.reveal.el);
    this.onTab();
  }

  // ------------------------------------------------------------------------------------------------ equipement

  private unlockedSlots(): boolean[] {
    const best = this.ctx.sim.data.progress.bestStageValue;
    return this.ctx.config.balance.equipSlotUnlocks.map((v) => best >= v);
  }

  private autoSlot(): number {
    const open = this.unlockedSlots();
    const eq = this.ctx.sim.data.weapons.equipped;
    const free = open.findIndex((ok, i) => ok && !eq[i]);
    if (free >= 0) return free;
    return Math.max(0, open.lastIndexOf(true));
  }

  private equip(uid: Uid, slot: number): void {
    const r = send(this.ctx, { type: 'weapon.equip', uid, slot });
    if (r.ok) this.selectedUid = null;
    this.update(0);
  }

  /**
   * Arme du coffre amenee sur un emplacement occupe : si les deux peuvent fusionner (meme grade), on demande « Fusionner » ou « Equiper »
   * (la fusion garde l'emplacement : le resultat y est equipe) ; sinon simple equipement / echange.
   */
  private equipOrMerge(uid: Uid, slot: number, anchor: HTMLElement): void {
    const current = this.ctx.sim.data.weapons.equipped[slot] ?? null;
    if (current === null || current === uid || !this.ctx.sim.query.canMerge(uid, current)) {
      this.equip(uid, slot);
      return;
    }
    const t = this.ctx.i18n;
    this.menu.open(anchor, [
      {
        label: t.t('weapons.merge'),
        onSelect: () => {
          send(this.ctx, { type: 'weapon.merge', a: current, b: uid });
          this.select(null);
        },
      },
      { label: t.t('common.equip'), onSelect: () => this.equip(uid, slot) },
    ]);
  }

  private select(uid: Uid | null): void {
    this.selectedUid = uid;
    this.update(0);
  }

  /** Menu contextuel d'une arme (appui long / clic droit) : verrou, equiper ou retirer. */
  private openMenu(uid: Uid, anchor: HTMLElement): void {
    const t = this.ctx.i18n;
    const view = this.ctx.sim.query.weapons().find((w) => w.item.uid === uid);
    if (!view) return;
    this.menu.open(anchor, [
      { label: view.item.locked ? t.t('weapons.unlock') : t.t('weapons.lock'), onSelect: () => void send(this.ctx, { type: 'weapon.lock', uid, locked: !view.item.locked }) },
      view.equippedSlot === null
        ? { label: t.t('common.equip'), onSelect: () => this.equip(uid, this.autoSlot()) }
        : { label: t.t('weapons.unequip'), onSelect: () => void send(this.ctx, { type: 'weapon.unequip', slot: view.equippedSlot as number }) },
    ]);
  }

  private canDrop(src: DragSource, t: DropTarget): boolean {
    if (src.slot === null) {
      if (t.kind === 'tile') return t.uid !== src.uid && this.ctx.sim.query.canMerge(src.uid, t.uid);
      return t.kind === 'slot' && !t.locked;
    }
    if (t.kind === 'slot') return !t.locked && t.slot !== src.slot;
    return true; // arme du coffre : echange ; zone du coffre : retrait
  }

  private dropOn(src: DragSource, t: DropTarget): void {
    if (src.slot === null) {
      if (t.kind === 'slot') {
        if (!t.locked) this.equipOrMerge(src.uid, t.slot, t.el);
        else notify(this.ctx.i18n.tDyn('error.locked'), 'warning');
      } else if (t.kind === 'tile' && t.uid !== src.uid) {
        // depot sur une autre arme : fusion ; si le core la refuse (grade different, verrou...), il l'explique par un message
        void send(this.ctx, { type: 'weapon.merge', a: src.uid, b: t.uid });
        this.select(null);
      }
      return;
    }
    if (t.kind === 'slot') {
      if (t.locked) notify(this.ctx.i18n.tDyn('error.locked'), 'warning');
      else if (t.slot !== src.slot) this.equip(src.uid, t.slot);
    } else if (t.kind === 'tile') {
      // echange (l'arme du coffre prend l'emplacement, l'autre retourne au coffre) ou fusion si les grades le permettent
      this.equipOrMerge(t.uid, src.slot, t.el);
    } else {
      void send(this.ctx, { type: 'weapon.unequip', slot: src.slot });
    }
    this.update(0);
  }

  private tapSlot(slot: number): void {    const open = this.unlockedSlots()[slot] === true;
    const current = this.ctx.sim.data.weapons.equipped[slot] ?? null;
    if (!open) {
      notify(this.ctx.i18n.tDyn('error.locked'), 'warning');
      return;
    }
    if (this.selectedUid) {
      if (this.selectedUid === current) this.selectedUid = null;
      else this.equipOrMerge(this.selectedUid, slot, this.slots[slot]?.root ?? this.el);
    } else {
      this.selectedUid = current;
    }
    this.update(0);
  }

  // ------------------------------------------------------------------------------------------------ cycle

  private onTab(): void {
    const active = this.tabs.active();
    for (const [id, pane] of this.panes) toggleClass(pane, 'active', id === active);
    this.update(0);
  }

  override update(dt: number): void {
    const { data, query } = this.ctx.sim;
    const { i18n, config } = this.ctx;

    // La selection ne survit pas a la disparition de l'arme (fusion).
    if (this.selectedUid && !data.weapons.items.some((w) => w.uid === this.selectedUid)) this.selectedUid = null;

    const open = this.unlockedSlots();
    for (const s of this.slots) {
      const uid = data.weapons.equipped[s.slot] ?? null;
      const item = uid ? data.weapons.items.find((w) => w.uid === uid) : undefined;
      const isOpen = open[s.slot] === true;
      s.root.dataset.locked = String(!isOpen);
      if (uid) s.root.dataset.uid = uid;
      else delete s.root.dataset.uid;
      toggleClass(s.root, 'locked', !isOpen);
      toggleClass(s.root, 'empty', isOpen && !item);
      toggleClass(s.root, 'selected', uid !== null && uid === this.selectedUid);
      toggleClass(s.root, 'target', isOpen && this.selectedUid !== null && uid !== this.selectedUid);
      const defId = item?.defId ?? null;
      if (s.shown !== defId) {
        s.shown = defId;
        s.tileHost.replaceChildren();
        if (item) {
          const tile = createTile(this.ctx, this.ctx.index.weapon(item.defId), 'mini');
          tile.root.removeAttribute('role');
          tile.root.removeAttribute('tabindex');
          s.tileHost.append(tile.root);
        }
      }
      toggleClass(s.lock, 'is-hidden', isOpen);
      toggleClass(s.stats, 'is-hidden', !item);
      if (!isOpen) {
        const need = config.balance.equipSlotUnlocks[s.slot] ?? 0;
        setText(s.lockText, `${i18n.t('common.unlockAt', { stage: stageValueToDisplay(config.balance, need).stage })}`);
      } else if (item) {
        const resolved = query.equippedWeapon(s.slot);
        if (resolved) {
          setText(s.dmg, formatBig(resolved.damage));
          setText(s.speed, `${formatShotsPerSecond(1 / resolved.interval)}/s`);
          setText(s.dps, formatBig(resolved.dps));
        }
      }
    }

    this.detail.update(dt);

    switch (this.tabs.active()) {
      case 'inventory':
        this.inventory.update(dt);
        break;
      case 'collection':
        this.collection.update(dt);
        break;
      case 'stars':
        this.stars.update(dt);
        break;
    }
  }
}
